import MemoryCache from "memory-cache";
import type { MailboxInfo } from "owa-client-types";
import { getIdentityInfoKeyForRequest } from "./getAnchorMailbox";
import type { AccountSourceType } from "owa-account-source-list-types";
import type { ClientTokenResponse } from "owa-tokenprovider-utils";
import type { IdentityInfo } from "owa-auth-types";

type AuthTokenCache = MemoryCache.Cache<string, ClientTokenResponse>;

let authTokenCache: AuthTokenCache | null = null;

/**
 * Gets auth token cache to be used for Native Host purposes only
 */
function getAuthTokenCache(): AuthTokenCache {
    if (authTokenCache == null) {
        authTokenCache = new MemoryCache.Cache();
    }
    
    return authTokenCache;
}

/**
 * Clears authtoken cache
 */
export function clearAuthTokenCache(): void {
    getAuthTokenCache().clear();
}

/**
 * Puts an authtoken to cache
 * @param tokenResponse Mandatory token response, must be a valid token with valid expiry
 * @param identitySource MailboxInfo or IdentityInfo object to extract cache key from
 * @param resource Resource for which we are trying to cache auth token
 * @param scope Scope for which we are trying to cache auth token
 * @param childAppId If we are caching a token for childAppId
 */
export function cacheAuthToken(tokenResponse: ClientTokenResponse, identitySource: MailboxInfo | IdentityInfo, resource?: string, scope?: string, childAppId?: string, callerTypeHint?: AccountSourceType): void {
    if (tokenResponse?.token) {
        getAuthTokenCache().put(getAuthTokenCacheKey(identitySource, resource, scope, childAppId, callerTypeHint), tokenResponse, getTtlInMs(tokenResponse?.expiresDur));
    }
}

/**
 * Gets authtoken cache key
 * @param identitySource MailboxInfo or IdentityInfo object to extract cache key from
 * @param resource Resource for which we are trying to fetch key
 * @param scope Scope for which we are trying to fetch key
 */
function getAuthTokenCacheKey(identitySource?: MailboxInfo | IdentityInfo, resource?: string, scope?: string, childAppId?: string, callerTypeHint?: AccountSourceType): string {
    let cacheKey: string = resource ?? "";
    if (identitySource) {
        const identityInfoKey = getIdentityInfoKeyForRequest(identitySource, callerTypeHint);
        cacheKey = cacheKey + "|" + identityInfoKey;
    }
    
    if (scope) {
        cacheKey = cacheKey + "|" + scope;
    }
    
    if (childAppId) {
        cacheKey = cacheKey + "|" + childAppId;
    }
    
    return cacheKey;
}

/**
 * Checks if incoming token is valid i.e. not null and has not expired
 * @param tokenResponse Token for which we are trying to check if it is valid
 */
export function isValidCachedToken(tokenResponse?: ClientTokenResponse): boolean {
    if (tokenResponse?.expiresDur && tokenResponse.expiresDur * 1000 > Date.now()) {
        return true;
    }
    
    return false;
}

/**
 * Gets authtoken from cache, to be used when there was an auth failure and we are about to retry
 * @param mailboxInfo MailboxInfo object to extract mailboxInfoKey from
 * @param resource Resource for which we are trying to fetch token
 * @param scope Scope for which we are trying to fetch token
 */
export function getCachedToken(mailboxInfo?: MailboxInfo, resource?: string, scope?: string, childAppId?: string, callerTypeHint?: AccountSourceType): ClientTokenResponse | undefined {
    const tokenResponse = getAuthTokenCache().get(getAuthTokenCacheKey(mailboxInfo, resource, scope, childAppId, callerTypeHint));
    return tokenResponse ?? undefined;
}

/**
 * Clears authtoken from cache, to be used when there was an auth failure and we are about to retry
 * @param resource Resource for which we are trying to clear auth token
 * @param identitySource MailboxInfo or IdentityInfo object to extract cache key from
 */
export function clearAuthToken(resource: string, identitySource?: MailboxInfo | IdentityInfo): void {
    const key = getAuthTokenCacheKey(identitySource, resource);
    getAuthTokenCache().del(key);
}

/**
 * Gets the TimeToLive in millisecs if expiresDur is present, returns undefined otherwise.
 * Undefined will keep the entry in cache until explicitly removed.
 * @param expiresDur ExpiresDur value in secs for the token
 */
function getTtlInMs(expiresDur?: number) {
    if (expiresDur) {
        return expiresDur * 1000 - Date.now();
    }
    
    return undefined;
}
